/**
 * Dialogue Completion Exercise
 * User completes missing lines in a dialogue, presented as chat bubbles.
 *
 * A dialogue has two kinds of gap, and the corpus uses both:
 *
 *   1. WHOLE LINE — `isBlank: true`. The bubble is replaced by an input (or by a
 *      selection, when `options` is supplied), and the learner produces the
 *      speaker's entire turn.
 *   2. INLINE — one or more `___` inside a non-blank line's `text`. The line
 *      renders normally with a small input in place of each `___`, and the
 *      learner supplies just the missing forms:
 *        B: "No, I ___. I've ___ tried it."   →  ["haven't", "never"]
 *
 * Only the first was implemented, which made 29 seeded exercises silently
 * inert: with no `isBlank` line the blank list came out empty, so the dialogue
 * rendered with literal "___" visible, offered no input at all, and — because
 * the scoring loop had nothing to iterate — reported 100% the moment it was
 * submitted. They were not failing loudly; they were passing without being
 * answered.
 *
 * Both kinds resolve to one flat, ordered list of SLOTS, and `correctAnswers[k]`
 * pairs with slot `k` in reading order. That ordering is what the content
 * already assumes: across all 29, the number of `___` matches the number of
 * answers exactly. `answers` is therefore keyed by slot index, NOT by line
 * index — a line can now own more than one.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { TouchableOpacity, TextInput, View } from 'react-native';
import { Box, Text, VStack } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { colors, FS_XS, FS_SM } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

const BLANK_TOKEN = '___';

interface DialogueLine {
  speaker: string;
  text: string | null;
  isBlank: boolean;
}

/** One answerable gap, in reading order. */
interface Slot {
  lineIndex: number;
  /** Whole-line slots replace the bubble; inline slots sit between text runs. */
  kind: 'line' | 'inline';
}

interface DialogueCompletionExerciseProps {
  prompt: string;
  dialogue: DialogueLine[];
  correctAnswers: string[];
  options?: string[];
  hint?: string;
  context?: string;
  onComplete: (correct: boolean, score: number) => void;
}

/** Text runs around each `___`; N blanks yield N+1 runs. */
function splitInline(text: string): string[] {
  return text.split(BLANK_TOKEN);
}

export const DialogueCompletionExercise = React.memo(function DialogueCompletionExercise({
  prompt,
  dialogue,
  correctAnswers,
  options,
  hint,
  context,
  onComplete,
}: DialogueCompletionExerciseProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();

  // Flatten both gap kinds into one ordered list. Slot k ↔ correctAnswers[k].
  const slots = useMemo(() => {
    const out: Slot[] = [];
    dialogue.forEach((line, lineIndex) => {
      if (line.isBlank) {
        // The bubble is replaced wholesale, so any `___` in its text is moot.
        out.push({ lineIndex, kind: 'line' });
        return;
      }
      const blanks = (line.text ?? '').split(BLANK_TOKEN).length - 1;
      for (let i = 0; i < blanks; i++) out.push({ lineIndex, kind: 'inline' });
    });
    return out;
  }, [dialogue]);

  /** Slot indices owned by a line, in order — inline lines can own several. */
  const slotsByLine = useMemo(() => {
    const map = new Map<number, number[]>();
    slots.forEach((slot, slotIndex) => {
      map.set(slot.lineIndex, [...(map.get(slot.lineIndex) ?? []), slotIndex]);
    });
    return map;
  }, [slots]);

  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  // Determine speakers for left/right alignment
  const speakers = useMemo(() => {
    const unique = [...new Set(dialogue.map((d) => d.speaker))];
    return { left: unique[0], right: unique[1] || unique[0] };
  }, [dialogue]);

  const handleTextChange = useCallback((slotIndex: number, text: string) => {
    setAnswers((prev) => ({ ...prev, [slotIndex]: text }));
  }, []);

  const handleOptionSelect = useCallback(
    (slotIndex: number, option: string) => {
      if (submitted) return;
      setAnswers((prev) => ({ ...prev, [slotIndex]: option }));
    },
    [submitted]
  );

  const isSlotCorrect = useCallback(
    (slotIndex: number) =>
      (answers[slotIndex] || '').toLowerCase().trim() ===
      (correctAnswers[slotIndex] || '').toLowerCase().trim(),
    [answers, correctAnswers]
  );

  const handleSubmit = useCallback(() => {
    if (submitted) return;
    // An exercise with no slots cannot be "all correct" — it was never answered.
    const allCorrect =
      slots.length > 0 &&
      slots.every(
        (_slot, slotIndex) =>
          (answers[slotIndex] || '').toLowerCase().trim() ===
          (correctAnswers[slotIndex] || '').toLowerCase().trim()
      );

    setIsCorrect(allCorrect);
    setSubmitted(true);
    onComplete(allCorrect, allCorrect ? 100 : 0);
  }, [submitted, slots, answers, correctAnswers, onComplete]);

  const allAnswered =
    slots.length > 0 && slots.every((_s, i) => (answers[i] || '').trim().length > 0);

  /** Options fill the first still-empty slot. */
  const targetSlot = useMemo(() => {
    const firstEmpty = slots.findIndex((_s, i) => !(answers[i] || '').trim());
    return firstEmpty >= 0 ? firstEmpty : slots.length - 1;
  }, [slots, answers]);

  const inputStyle = {
    fontSize: FS_SM * fontScale,
    color: theme.text,
    padding: 0,
    minWidth: 120,
    ...ffs,
  };

  // An inline input needs an explicit width. A bare TextInput carries a ~165px
  // intrinsic width, which on a ~223px bubble row swallows the whole line and
  // pushes the surrounding words onto lines of their own — the sentence stops
  // reading as a sentence. Width is derived from the longest inline answer and
  // applied to EVERY inline blank, so the gaps stay uniform and none of them
  // telegraphs how long its own answer is.
  const inlineInputWidth = useMemo(() => {
    const longest = slots.reduce(
      (max, slot, slotIndex) =>
        slot.kind === 'inline' ? Math.max(max, (correctAnswers[slotIndex] ?? '').length) : max,
      0
    );
    return Math.min(132, Math.max(56, longest * 8 + 14)) * fontScale;
  }, [slots, correctAnswers, fontScale]);

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
        >
          {prompt}
        </Text>

        {context && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {context}
          </Text>
        )}

        {hint && !submitted && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Dialogue bubbles */}
        <VStack space="sm">
          {dialogue.map((line, idx) => {
            const isLeft = line.speaker === speakers.left;
            const isBlank = line.isBlank;
            const lineSlots = slotsByLine.get(idx) ?? [];
            const lineSlot = isBlank ? lineSlots[0] : undefined;
            const userAnswer = lineSlot !== undefined ? answers[lineSlot] || '' : '';
            const wholeLineCorrect = lineSlot !== undefined && isSlotCorrect(lineSlot);

            let bubbleBg: string;
            let bubbleBorder: string = 'transparent';

            if (isBlank && submitted) {
              bubbleBg = wholeLineCorrect ? '#D1FAE5' : '#FEE2E2';
              bubbleBorder = wholeLineCorrect ? '#059669' : '#DC2626';
            } else if (isBlank) {
              bubbleBg = theme.primarySoft;
              bubbleBorder = theme.primary;
            } else {
              bubbleBg = isLeft
                ? (theme.isDark ? '#374151' : '#F3F4F6')
                : (theme.isDark ? '#1E3A5F' : '#EFF6FF');
            }

            return (
              <Box
                key={idx}
                alignSelf={isLeft ? 'flex-start' : 'flex-end'}
                style={{ maxWidth: '80%' }}
              >
                {/* Speaker label */}
                <Text
                  allowFontScaling={false}
                  style={{
                    fontSize: FS_XS * fontScale,
                    color: theme.textSecondary,
                    fontWeight: '600',
                    marginBottom: 2,
                    textAlign: isLeft ? 'left' : 'right',
                    ...ffs,
                  }}
                >
                  {line.speaker}
                </Text>

                <Box
                  px="$3"
                  py="$2"
                  rounded="$lg"
                  style={{
                    backgroundColor: bubbleBg,
                    borderWidth: isBlank ? 1 : 0,
                    borderColor: bubbleBorder,
                    borderBottomLeftRadius: isLeft ? 4 : 16,
                    borderBottomRightRadius: isLeft ? 16 : 4,
                  }}
                >
                  {isBlank && !submitted && lineSlot !== undefined ? (
                    options ? (
                      <Text
                        allowFontScaling={false}
                        style={{
                          fontSize: FS_SM * fontScale,
                          color: userAnswer ? theme.primary : theme.textSecondary,
                          fontWeight: userAnswer ? '500' : '400',
                          fontStyle: userAnswer ? 'normal' : 'italic',
                          ...ffs,
                        }}
                      >
                        {userAnswer || t('exercises.dialogueCompletion.selectAnswer')}
                      </Text>
                    ) : (
                      <TextInput
                        testID={`dialogue-input-${lineSlot}`}
                        accessibilityLabel={t('exercises.dialogueCompletion.answerForA11y', {
                          speaker: line.speaker,
                        })}
                        accessibilityRole="text"
                        value={userAnswer}
                        onChangeText={(text) => handleTextChange(lineSlot, text)}
                        placeholder={t('exercises.dialogueCompletion.typeAnswer')}
                        placeholderTextColor={theme.textSecondary}
                        returnKeyType="done"
                        onSubmitEditing={handleSubmit}
                        style={inputStyle}
                        allowFontScaling={false}
                      />
                    )
                  ) : isBlank ? (
                    <Text
                      allowFontScaling={false}
                      style={{
                        fontSize: FS_SM * fontScale,
                        color: wholeLineCorrect ? '#065F46' : '#991B1B',
                        fontWeight: '500',
                        ...ffs,
                      }}
                    >
                      {userAnswer}
                    </Text>
                  ) : lineSlots.length > 0 ? (
                    // Inline blanks: text runs interleaved with per-gap inputs,
                    // wrapping like a paragraph so a long turn still reads as one.
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'baseline' }}>
                      {splitInline(line.text ?? '').map((run, runIdx, runs) => {
                        const slotIndex = lineSlots[runIdx];
                        const answered = slotIndex !== undefined ? answers[slotIndex] || '' : '';
                        const slotCorrect = slotIndex !== undefined && isSlotCorrect(slotIndex);
                        return (
                          <React.Fragment key={runIdx}>
                            {run.length > 0 && (
                              <Text
                                allowFontScaling={false}
                                style={{
                                  fontSize: FS_SM * fontScale,
                                  color: theme.text,
                                  fontWeight: '500',
                                  ...ffs,
                                }}
                              >
                                {run}
                              </Text>
                            )}
                            {runIdx < runs.length - 1 && slotIndex !== undefined && (
                              submitted ? (
                                <Text
                                  allowFontScaling={false}
                                  style={{
                                    fontSize: FS_SM * fontScale,
                                    fontWeight: '700',
                                    color: slotCorrect ? '#065F46' : '#991B1B',
                                    textDecorationLine: slotCorrect ? 'none' : 'line-through',
                                    ...ffs,
                                  }}
                                >
                                  {answered || BLANK_TOKEN}
                                </Text>
                              ) : (
                                <TextInput
                                  testID={`dialogue-input-${slotIndex}`}
                                  accessibilityLabel={t('exercises.dialogueCompletion.blankA11y', {
                                    number: slotIndex + 1,
                                    speaker: line.speaker,
                                  })}
                                  accessibilityRole="text"
                                  value={answered}
                                  onChangeText={(text) => handleTextChange(slotIndex, text)}
                                  // The underline already marks the gap; a "___"
                                  // placeholder on top of it just reads as two rules.
                                  placeholder=""
                                  placeholderTextColor={theme.textSecondary}
                                  returnKeyType="done"
                                  onSubmitEditing={handleSubmit}
                                  style={{
                                    ...inputStyle,
                                    width: inlineInputWidth,
                                    minWidth: undefined,
                                    flexGrow: 0,
                                    flexShrink: 0,
                                    marginHorizontal: 2,
                                    textAlign: 'center',
                                    // The underline carries the "this is a gap"
                                    // signal; the answer itself stays body text.
                                    // Tinting the text primary instead put teal
                                    // #0D9488 on the bubble at 3.4:1 in light and
                                    // 3.1:1 in dark — under AA for text either way,
                                    // while a border only needs 3:1.
                                    borderBottomWidth: 1,
                                    borderBottomColor: theme.primary,
                                    color: theme.text,
                                    fontWeight: '700',
                                  }}
                                  allowFontScaling={false}
                                />
                              )
                            )}
                          </React.Fragment>
                        );
                      })}
                    </View>
                  ) : (
                    <Text
                      allowFontScaling={false}
                      style={{
                        fontSize: FS_SM * fontScale,
                        color: theme.text,
                        fontWeight: '500',
                        ...ffs,
                      }}
                    >
                      {line.text}
                    </Text>
                  )}
                </Box>
              </Box>
            );
          })}
        </VStack>

        {/* Options (multiple choice) */}
        {options && !submitted && (
          <VStack space="xs">
            {options.map((option, idx) => {
              const isSelected = Object.values(answers).includes(option);

              return (
                <TouchableOpacity
                  key={idx}
                  testID={`dialogue-option-${idx}`}
                  accessibilityLabel={option}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected }}
                  onPress={() => handleOptionSelect(targetSlot, option)}
                  disabled={submitted}
                  activeOpacity={0.7}
                >
                  <Box
                    px="$4"
                    py="$3"
                    rounded="$md"
                    style={{
                      backgroundColor: isSelected ? colors.primary : (theme.isDark ? '#374151' : '#F3F4F6'),
                      borderWidth: 1,
                      borderColor: isSelected ? colors.primary : theme.border,
                    }}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{
                        fontSize: FS_SM * fontScale,
                        fontWeight: '500',
                        color: isSelected ? '#FFFFFF' : theme.text,
                        ...ffs,
                      }}
                    >
                      {option}
                    </Text>
                  </Box>
                </TouchableOpacity>
              );
            })}
          </VStack>
        )}

        {/* Submit button */}
        {!submitted && allAnswered && (
          <TouchableOpacity
            testID="dialogue-submit"
            accessibilityLabel={t('exercises.dialogueCompletion.checkAnswer')}
            accessibilityRole="button"
            onPress={handleSubmit}
            activeOpacity={0.7}
          >
            <Box
              py="$3"
              rounded="$md"
              alignItems="center"
              style={{ backgroundColor: colors.primary }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#FFFFFF', ...ffs }}
              >
                {t('exercises.dialogueCompletion.checkAnswer')}
              </Text>
            </Box>
          </TouchableOpacity>
        )}

        {/* Result */}
        {submitted && (
          <VStack space="xs">
            <Box
              p="$2"
              rounded="$md"
              style={{ backgroundColor: isCorrect ? '#D1FAE5' : '#FEE2E2' }}
            >
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_SM * fontScale,
                  fontWeight: '600',
                  color: isCorrect ? '#065F46' : '#991B1B',
                  ...ffs,
                }}
              >
                {isCorrect
                  ? `✓ ${t('exercises.dialogueCompletion.correct')}`
                  : `✗ ${t('exercises.dialogueCompletion.notQuite')}`}
              </Text>
            </Box>

            {!isCorrect && (
              <VStack space="xs">
                {slots.map((_slot, slotIndex) => {
                  if (isSlotCorrect(slotIndex)) return null;
                  return (
                    <Text
                      key={slotIndex}
                      allowFontScaling={false}
                      style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
                    >
                      {t('exercises.dialogueCompletion.expected', {
                        answer: correctAnswers[slotIndex] || '',
                      })}
                    </Text>
                  );
                })}
              </VStack>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
});
